'use client';

import {
  BadgeCheck,
  Bell,
  ChevronsUpDown,
  LogOut,
  Sparkles,
  Mail,
} from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdownMenu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar/sidebar';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/providers/authProvider';
import { useIsCustomerUser, usePortalAccess } from '@/providers/userProvider';

export function NavUser({
  user,
}: {
  user: {
    name: string;
    email: string;
    avatar: string;
    title?: string;
    phone?: string;
  };
}) {
  const { isMobile } = useSidebar();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const isCustomerUser = useIsCustomerUser();
  const { portalAccess, isSubscriber } = usePortalAccess();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getSubscriptionLabel = () => {
    if (!isCustomerUser) return null;
    if (portalAccess === 'subscriber') return 'Subscriber';
    if (portalAccess === 'free') return 'Free Plan';
    return null;
  };

  const subscriptionLabel = getSubscriptionLabel();

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-base-link data-[state=open]:text-sidebar-accent-foreground text-white hover:bg-bg-base-link"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={user.avatar} alt={user.name} />
                <AvatarFallback className="rounded-lg bg-base-link text-white text-xs">
                  {(user.name || 'MC')
                    .split(' ')
                    .map((w) => w[0])
                    .slice(0, 2)
                    .join('')
                    .toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium text-white">
                  {user.name}
                </span>
                {user.title && (
                  <span className="truncate text-xs text-white/80">
                    {user.title}
                  </span>
                )}
                <span className="truncate text-xs text-white">
                  {user.email}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage src={user.avatar} alt={user.name} />
                  <AvatarFallback className="rounded-lg bg-base-link text-white text-xs">
                  {(user.name || 'MC')
                    .split(' ')
                    .map((w) => w[0])
                    .slice(0, 2)
                    .join('')
                    .toUpperCase()}
                </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{user.name}</span>
                  {user.title && (
                    <span className="truncate text-xs text-muted-foreground">{user.title}</span>
                  )}
                  <span className="truncate text-xs">{user.email}</span>
                  {user.phone && (
                    <span className="truncate text-xs text-muted-foreground">{user.phone}</span>
                  )}
                  {subscriptionLabel && (
                    <span className={`text-xs mt-0.5 ${isSubscriber ? 'text-blue-600' : 'text-gray-500'}`}>
                      {subscriptionLabel}
                    </span>
                  )}
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />

            {/* Customer Portal Menu Items */}
            {isCustomerUser && (
              <>
                {!isSubscriber && (
                  <DropdownMenuGroup>
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={() => {
                        window.location.href = 'mailto:service@assembledproducts.com?subject=Portal Subscription Inquiry';
                      }}
                    >
                      <Sparkles />
                      Upgrade to Subscriber
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                )}
                <DropdownMenuGroup>
                  <DropdownMenuItem>
                    <Mail />
                    Weekly Digest Settings
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
              </>
            )}

            {/* Internal User Menu Items */}
            {!isCustomerUser && (
              <>
                <DropdownMenuGroup>
                  <DropdownMenuItem>
                    <BadgeCheck />
                    Account
                  </DropdownMenuItem>
                  <DropdownMenuItem>
                    <Bell />
                    Notifications
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
              </>
            )}

            <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
              <LogOut />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
